import { BookedService, Product } from "../types/transactions";
import { calculateDiscountedItemPrice } from "./discountUtils";
import { Employee, BusinessChargeCommissionMode } from "../types/salon";
import {
  CommissionRecord,
  AmountType,
  CalculationMethod,
  CommissionTier,
  CommissionTiers,
  CommissionTierDraft,
  CommissionTierDrafts,
  TierBucket,
} from "../types/commission";

/**
 * Calculate a GROSS service commission on the frontend from the employee's
 * current rate. NOTE: this does NOT subtract any per-service business charge,
 * so it can diverge from the backend once a charge is configured — prefer the
 * backend `calc__commission_amount` (via getEffectiveServiceCommission) for
 * display. Retained for tests / legacy previews.
 * @param service - The booked service
 * @param employee - The employee who performed the service
 * @param enableDiscountDeduction - Whether to deduct discounts before calculating commission
 * @returns The calculated commission amount
 */
export const calculateServiceCommission = (
  service: BookedService,
  employee: Employee | undefined,
  enableDiscountDeduction: boolean = false,
): number => {
  if (!employee) return 0;

  // If no commission settings, return 0
  if (
    !employee.commission_type_services ||
    !employee.commission_amount_services
  ) {
    return 0;
  }

  // The commission base: the line price, net of all its discounts when
  // discount deduction is enabled.
  const baseAmount = enableDiscountDeduction
    ? calculateDiscountedItemPrice(service)
    : service.calc__cost_excl_tips_discounts;

  // Calculate commission based on type
  if (employee.commission_type_services === "Percentage") {
    return (baseAmount * (employee.commission_amount_services || 0)) / 100;
  } else if (employee.commission_type_services === "Fixed Amount") {
    return employee.commission_amount_services || 0;
  }

  return 0;
};

/**
 * Calculate a GROSS product commission on the frontend from the employee's
 * current rate. NOTE: this does NOT subtract any per-product business charge,
 * so it can diverge from the backend once a charge is configured — prefer the
 * backend `calc__commission_amount` (via getEffectiveProductCommission) for
 * display. Retained for tests / legacy previews.
 * @param product - The product sold
 * @param employee - The employee who sold the product
 * @param enableDiscountDeduction - Whether to deduct discounts before calculating commission
 * @returns The calculated commission amount
 */
export const calculateProductCommission = (
  product: Product,
  employee: Employee | undefined,
  enableDiscountDeduction: boolean = false,
): number => {
  if (!employee) return 0;

  // If no commission settings, return 0
  if (
    !employee.commission_type_products ||
    !employee.commission_amount_products
  ) {
    return 0;
  }

  // The commission base: the line price, net of all its discounts when
  // discount deduction is enabled.
  const baseAmount = enableDiscountDeduction
    ? calculateDiscountedItemPrice(product, product.quantity)
    : product.unit_price * product.quantity;

  // Calculate commission based on type
  if (employee.commission_type_products === "Percentage") {
    return (baseAmount * (employee.commission_amount_products || 0)) / 100;
  } else if (employee.commission_type_products === "Fixed Amount") {
    return employee.commission_amount_products || 0;
  }

  return 0;
};

/**
 * Get the effective commission amount for a service.
 *
 * The backend `calc__commission_amount` is authoritative: it already nets the
 * salon's discount-deduction setting AND any per-service business charge. A
 * frontend recompute would miss the business charge (it is not exposed on the
 * line item), so we always trust the backend value. The `employee` /
 * `enableDiscountDeduction` params are retained for call-site compatibility.
 * @param service - The booked service (source of the backend commission)
 * @returns The commission amount to display
 */
export const getEffectiveServiceCommission = (
  service: BookedService,
  _employee?: Employee | undefined,
  _enableDiscountDeduction: boolean = false,
): number => {
  return service.calc__commission_amount || 0;
};

/**
 * Get the effective commission amount for a product.
 *
 * The backend `calc__commission_amount` is authoritative: it already nets the
 * salon's discount-deduction setting AND any per-product business charge. A
 * frontend recompute would miss the business charge (it is not exposed on the
 * line item), so we always trust the backend value. The `employee` /
 * `enableDiscountDeduction` params are retained for call-site compatibility.
 * @param product - The product line item (source of the backend commission)
 * @returns The commission amount to display
 */
export const getEffectiveProductCommission = (
  product: Product,
  _employee?: Employee | undefined,
  _enableDiscountDeduction: boolean = false,
): number => {
  return product.calc__commission_amount || 0;
};

/**
 * The default commission target (earner) for a NEW product line item.
 *
 * A product sale has no performer, so commission defaults to the staff member on
 * the transaction's FIRST booked service (the appointment's provider). When
 * there is no service — e.g. a walk-in quick sale — it falls back to the
 * logged-in user (the seller). Returns null when neither is available.
 *
 * Unlike the (retired) `determineCommissionTarget`, this does NOT depend on the
 * legacy `commission_amount_products` staff field, which the multi-location
 * refactor renamed on the wire — so the default resolves correctly on live data.
 *
 * @param bookedServices - The transaction's booked services (may be empty/absent)
 * @param currentUserId - The logged-in staff member's id
 * @returns The staff id to attribute product commission to, or null
 */
export const getDefaultCommissionTarget = (
  bookedServices: BookedService[] | undefined | null,
  currentUserId?: string | null,
): string | null => {
  return bookedServices?.[0]?.staff_id || currentUserId || null;
};

// ============================================================
// New Commission API helpers (CommissionRecord-based)
// ============================================================

/**
 * Get the active commission for a staff member.
 * Active = the record with the most recent effective_start_date <= today.
 */
export const getActiveCommission = (
  commissions: CommissionRecord[],
): CommissionRecord | null => {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const eligible = commissions.filter((c) => c.effective_start_date <= today);
  if (eligible.length === 0) return null;
  // Sort descending by effective_start_date, return the most recent
  eligible.sort((a, b) =>
    b.effective_start_date.localeCompare(a.effective_start_date),
  );
  return eligible[0];
};

/**
 * Group an array of commission records by staff_id.
 */
export const groupCommissionsByStaff = (
  commissions: CommissionRecord[],
): Record<string, CommissionRecord[]> => {
  return commissions.reduce(
    (acc, commission) => {
      const key = commission.staff_id;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(commission);
      return acc;
    },
    {} as Record<string, CommissionRecord[]>,
  );
};

/**
 * Format a commission amount for display (e.g., "15%" or "$25").
 * Returns null if amount is null/undefined (not configured).
 */
export const formatCommissionAmount = (
  amount: number | null | undefined,
  amountType: AmountType | null | undefined,
): string | null => {
  if (amount === null || amount === undefined || !amountType) return null;
  if (amountType === "Percentage") {
    return `${amount}%`;
  }
  return amount % 1 === 0 ? `$${amount}` : `$${amount.toFixed(2)}`;
};

// ============================================================
// Commissions › Advanced Settings (salon-level toggles)
// ============================================================

/**
 * Editable values for the Commissions › Advanced Settings tab. These are
 * salon-level fields persisted via PUT /business-locations — the same record the rest of
 * Settings edits — but surfaced here because they govern commission behavior.
 */
export interface CommissionAdvancedSettingsValues {
  enable_discount_deduction_on_commissions: boolean;
  business_charge_commission_mode: BusinessChargeCommissionMode;
}

/** Salon record → editable Commissions Advanced Settings values. */
export const normalizeCommissionAdvancedSettings = (
  data: any,
): CommissionAdvancedSettingsValues => ({
  enable_discount_deduction_on_commissions:
    data?.enable_discount_deduction_on_commissions || false,
  business_charge_commission_mode:
    data?.business_charge_commission_mode || "sale_value",
});

/**
 * Commissions Advanced Settings values → partial salon payload for PUT /business-locations.
 * Only this view's fields are included so nothing else on the salon record is
 * touched by the update.
 */
export const buildCommissionAdvancedSettingsPayload = (
  values: CommissionAdvancedSettingsValues,
): Record<string, any> => ({
  enable_discount_deduction_on_commissions:
    values.enable_discount_deduction_on_commissions,
  business_charge_commission_mode: values.business_charge_commission_mode,
});

/**
 * Format a calculation method for display.
 */
export const formatCalculationMethod = (method: CalculationMethod): string => {
  switch (method) {
    case "STANDARD":
      return "Standard";
    case "COMMISSION_ONLY":
      return "Commission Only";
    case "GREATER_THAN":
      return "Greater Than";
    case "NUM_SERVICES":
      return "Threshold";
    case "TIERED":
      return "Tiered";
    default:
      return method;
  }
};

// ============================================================
// Tiered / Step commissions
// ============================================================

export const TIER_BUCKETS: TierBucket[] = ["services", "products", "packages"];

export const TIER_BUCKET_LABELS: Record<TierBucket, string> = {
  services: "Services",
  products: "Products",
  packages: "Packages / Specials",
};

/**
 * Validate a TIERED schedule client-side (mirrors the backend rules): per
 * bucket 1–10 contiguous steps starting at $0, only the last unbounded, each
 * rate 0–100. Returns the first human-readable error, or null when valid.
 */
export const validateTierSchedule = (tiers: CommissionTiers): string | null => {
  for (const bucket of TIER_BUCKETS) {
    const list = tiers[bucket];
    if (!list || list.length === 0) continue; // blank bucket = 0% commission
    const label = TIER_BUCKET_LABELS[bucket];
    if (list.length > 10) return `${label}: at most 10 tiers are allowed.`;
    const sorted = [...list].sort((a, b) => a.min - b.min);
    if (sorted[0].min !== 0)
      return `${label}: the first tier must start at $0.`;
    for (let i = 0; i < sorted.length; i++) {
      const tier = sorted[i];
      if (tier.rate < 0 || tier.rate > 100)
        return `${label}: each rate must be between 0% and 100%.`;
      const isLast = i === sorted.length - 1;
      if (tier.max !== null && tier.max <= tier.min)
        return `${label}: each tier's upper limit must be greater than its lower limit.`;
      if (isLast) continue;
      if (tier.max === null)
        return `${label}: only the last tier can be unlimited.`;
      if (sorted[i + 1].min !== tier.max)
        return `${label}: tiers must be contiguous with no gaps.`;
    }
  }
  const anyConfigured = TIER_BUCKETS.some((b) => (tiers[b]?.length ?? 0) > 0);
  if (!anyConfigured) return "Add at least one commission tier.";
  return null;
};

/** One bucket's rate progression for a table cell (e.g. "10% → 20%"), or null
 * when that bucket has no tiers. */
export const formatBucketTierRates = (
  tiers: CommissionTiers | undefined,
  bucket: TierBucket,
): string | null => {
  const list = tiers?.[bucket];
  if (!list || list.length === 0) return null;
  return list.map((t) => `${t.rate}%`).join(" → ");
};

/** Compact one-line summary of a tiered schedule for tables (e.g.
 * "Services: 10% → 20%; Packages: 5%"). */
export const formatTierSummary = (
  tiers: CommissionTiers | undefined,
): string => {
  if (!tiers) return "—";
  const parts = TIER_BUCKETS.filter((b) => (tiers[b]?.length ?? 0) > 0).map(
    (b) => {
      const rates = (tiers[b] as CommissionTier[]).map((t) => `${t.rate}%`);
      return `${TIER_BUCKET_LABELS[b]}: ${rates.join(" → ")}`;
    },
  );
  return parts.length ? parts.join("; ") : "—";
};

// ============================================================
// Tiered / Step — editable "draft" model (builder ⇄ wire)
// ============================================================
//
// The builder edits each step's lower bound (`from`) + `rate`; the upper bound
// ("Up to") is derived from the next step's `from`. Row 0 of every bucket is the
// base step (`from` fixed at 0) and is never removed. These helpers convert
// between that draft model and the wire `CommissionTiers` ({min,max,rate}) shape
// the API round-trips — the wire contract is unchanged, so payroll/reports are
// unaffected.

const tierNum = (v: number | ""): number =>
  v === "" ? NaN : typeof v === "number" ? v : Number(v);

/** Seed the builder from a saved wire schedule (edit mode). Each bucket always
 * gets at least the base step so the table is never empty; `from = min`, and the
 * derived `max` is dropped. */
export const wireToDrafts = (
  tiers: CommissionTiers | null | undefined,
): CommissionTierDrafts => {
  const drafts: CommissionTierDrafts = {};
  for (const bucket of TIER_BUCKETS) {
    const rows = tiers?.[bucket];
    drafts[bucket] =
      rows && rows.length > 0
        ? rows.map((t) => ({ from: t.min, rate: t.rate }))
        : [{ from: 0, rate: "" }];
  }
  return drafts;
};

/** Convert the editable drafts to the wire schedule for submit: `min = from`,
 * `max = next.from` (exclusive; `null` on the last step) → contiguous by
 * construction. A bucket whose only step is the base with no positive rate is
 * omitted (≡ blank bucket = 0%). Assumes the drafts already passed
 * `validateTierDrafts`. */
export const draftsToWire = (drafts: CommissionTierDrafts): CommissionTiers => {
  const wire: CommissionTiers = {};
  for (const bucket of TIER_BUCKETS) {
    const rows = drafts[bucket];
    if (!rows || rows.length === 0) continue;
    const baseRate = rows[0].rate === "" ? 0 : Number(rows[0].rate);
    // A lone base step at 0% carries no schedule — treat as "not configured".
    if (rows.length === 1 && !baseRate) continue;
    wire[bucket] = rows.map((row, i) => {
      const next = rows[i + 1];
      return {
        min: i === 0 ? 0 : Number(row.from),
        max: next ? Number(next.from) : null,
        rate: row.rate === "" ? 0 : Number(row.rate),
      };
    });
  }
  return wire;
};

/** Structural validity of a single bucket's steps (mirrors the design's
 * per-category check). A lone base step is always valid. With 2+ steps every
 * non-base `from` and every `rate` must be a valid number, rates 0–100, and the
 * `from` values strictly increasing (so each derived upper bound exceeds its
 * lower bound). Returns the first human-readable error, else null. */
export const tierCategoryError = (
  rows: CommissionTierDraft[] | undefined,
): string | null => {
  if (!rows || rows.length <= 1) return null;
  let prevFrom = 0; // base step starts at 0
  for (let i = 0; i < rows.length; i++) {
    // A blank rate means 0% — only a real number out of 0–100 is invalid.
    const rate = rows[i].rate === "" ? 0 : Number(rows[i].rate);
    if (Number.isNaN(rate) || rate < 0 || rate > 100)
      return "Each tier's rate must be between 0% and 100%.";
    if (i > 0) {
      const from = tierNum(rows[i].from);
      if (Number.isNaN(from))
        return "Fill in every tier's starting amount before saving.";
      if (from <= prevFrom)
        return "Each tier must start above the previous tier.";
      prevFrom = from;
    }
  }
  return null;
};

/** Form-level validity for a TIERED schedule: every configured bucket must be
 * structurally valid AND at least one bucket must carry a real schedule.
 * Returns the first human-readable error (bucket-prefixed), else null. Drives
 * the submit guard in NewCommissionForm. */
export const validateTierDrafts = (
  drafts: CommissionTierDrafts,
): string | null => {
  for (const bucket of TIER_BUCKETS) {
    const err = tierCategoryError(drafts[bucket]);
    if (err) return `${TIER_BUCKET_LABELS[bucket]}: ${err}`;
  }
  const wire = draftsToWire(drafts);
  const anyConfigured = TIER_BUCKETS.some((b) => (wire[b]?.length ?? 0) > 0);
  if (!anyConfigured) return "Add at least one commission tier.";
  return null;
};
